import { useMutation } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { ChatRecord, ClientToolSpec, RobotoLLMContext } from "../ChatRecord";

interface StartChatParams {
  message: string;
  orgId: string;
  context?: RobotoLLMContext;
  modelProfile?: string;
  clientTools?: ClientToolSpec[];
}

/**
 * React Query hook for starting a new chat session.
 *
 * @returns Mutation object with mutate/mutateAsync functions and loading/error states
 *
 * @example
 * ```tsx
 * const startChat = useStartChat();
 *
 * const handleStartChat = async () => {
 *   try {
 *     const chatRecord = await startChat.mutateAsync({
 *       message: "Hello, how can you help me?",
 *       orgId: "org_123"
 *     });
 *     console.log("Chat started:", chatRecord.session_id);
 *   } catch (error) {
 *     console.error("Failed to start chat:", error);
 *   }
 * };
 * ```
 */
export function useStartChat() {
  const { chatService } = useDomainServices();

  return useMutation<ChatRecord, Error, StartChatParams>({
    mutationFn: async ({
      message,
      orgId,
      context,
      modelProfile,
      clientTools,
    }) => {
      return await chatService.startChat(message, {
        orgId,
        context,
        modelProfile,
        clientTools,
      });
    },
  });
}
